import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    Input,
    InputLabel,
    Stack,
    TextField,
} from '@mui/material';
import { useState } from 'react';
import { useAppDispatch } from '../../../store/store';
import { setShowMessage, setIsLoading } from '../../../store/shared.slice';
import { Phone } from '../../../Models/Shared/Phone.model';
import TextMaskCustom from '../../Shared/PhoneMask';
import axios from 'axios';
import { apiString } from '../../../utils/constants';

interface SchedulingAddPhoneModalProps {
    open: boolean;
    onClose: () => void;
    memberId: string;
    currentPhones: Phone[];
    onPhoneAdded: () => void;
}

const SchedulingAddPhoneModal: React.FC<SchedulingAddPhoneModalProps> = ({ open, onClose, memberId, currentPhones, onPhoneAdded }) => {
    const dispatch = useAppDispatch();
    const [phone, setPhone] = useState<Phone>(new Phone());
    const [isValid, setIsValid] = useState(true);
    const [helperText, setHelperText] = useState('');

    const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPhone({ ...phone, Number: event.target.value });
    };

    const handleSave = async () => {
        const phonePattern = /^\(\d{3}\) \d{3}-\d{4}$/;

        if (!phonePattern.test(phone.Number)) {
            setHelperText('Please enter a valid phone number');
            setIsValid(false);
            return;
        } else if (currentPhones?.some((p) => p.Number === phone.Number)) {
            setHelperText('This phone number already exists');
            setIsValid(false);
            return;
        } else {
            setIsValid(true);
        }

        try {
            dispatch(setIsLoading(true));
            await axios.post(`${apiString}/Scheduling/AddPhoneToMember`, {
                memberId: memberId,
                phone: phone
            });
            dispatch(setShowMessage(true, 'Phone number added successfully', 'success'));
            handleClose();
            onPhoneAdded();
        } catch (error) {
            console.error('Error adding phone:', error);
            dispatch(setShowMessage(true, 'Error adding phone number', 'error'));
        } finally {
            dispatch(setIsLoading(false));
        }
    };

    const handleClose = () => {
        setPhone(new Phone());
        setIsValid(true);
        setHelperText('');
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="add-phone-title"
            maxWidth="md"
        >
            <DialogTitle id="add-phone-title">Add Phone Number</DialogTitle>
            <DialogContent>
                <Stack spacing={2} paddingTop={2}>
                    <Stack direction="row" spacing={2}>
                        <FormGroup>
                            <FormControl variant="standard" style={{ marginRight: '16px' }}>
                                <InputLabel htmlFor="phone-input">Phone Number</InputLabel>
                                <Input
                                    value={phone.Number || ''}
                                    onChange={handlePhoneChange}
                                    name="textmask"
                                    id="phone-input"
                                    inputComponent={TextMaskCustom as any}
                                    error={!isValid}
                                    required
                                />
                                <FormHelperText error={!isValid}>
                                    {isValid ? '' : helperText}
                                </FormHelperText>
                            </FormControl>
                        </FormGroup>
                        <FormGroup>
                            <FormControlLabel
                                control={<Checkbox checked={phone.Textable} />}
                                onChange={(e: any) => {
                                    setPhone({ ...phone, Textable: e.target.checked });
                                }}
                                label="Textable"
                            />
                        </FormGroup>
                        <FormGroup>
                            <FormControlLabel
                                control={<Checkbox checked={phone.IsPrimary} />}
                                onChange={(e: any) => {
                                    setPhone({ ...phone, IsPrimary: e.target.checked });
                                }}
                                label="Primary Phone"
                            />
                        </FormGroup>
                    </Stack>
                    <TextField
                        onChange={(e: any) => {
                            setPhone({ ...phone, Note: e.target.value });
                        }}
                        style={{ width: '100%' }}
                        id="phone-note"
                        label="Note"
                        multiline
                        rows={3}
                        value={phone.Note || ''}
                    />
                    <Box>
                        <Stack direction="row" spacing={2} style={{ float: 'right' }}>
                            <Button variant="contained" onClick={handleSave}>
                                Save
                            </Button>
                            <Button variant="outlined" onClick={handleClose}>
                                Cancel
                            </Button>
                        </Stack>
                    </Box>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export default SchedulingAddPhoneModal;
